import type { CiPLibrary, CiPLibraryCallbackResponse, StepData, RunCodeOptions, RunResult, CacheState, AdvancedAutograderState } from "./types/general";
// Use inline workers so the built package works when consumed by other bundlers (CRA/webpack)
import PythonWorker from "./python/worker?worker&inline";
import CanvasWorker from "./canvas/worker?worker&inline";
import { CappedMap } from "./utils/cappedMap";
import { StepVariables } from "./types/general";
import { addApiDataToTrace } from "./utils/traceApiData";
import { type AwaitablePromise, getAwaitablePromise } from "./utils/general";
// Re-export for backward compatibility
import { MainToWorkerEvent, WorkerToMainEvent, MainToCanvasEvent, CanvasAndWorkerEvent } from "./types/workerEvents";
export { MainToWorkerEvent, WorkerToMainEvent, CanvasAndWorkerEvent, MainToCanvasEvent } from "./types/workerEvents";
import type { SockCallbacks } from "./LangPlug";
import { writeMessage, makeServiceWorkerChannel, type Channel } from "./sync/syncMessage";
import { mergeCanvasIntoTrace } from "./python/zipTraceDataAndGraphics";
import type {
  GraphicsShape,
  ShapeId,
} from "./python/pyodide/GraphicsLib/graphicsTypes";
import {
  scheduleHangWatchdog,
  type RunCheckpoint,
  type RunHangReportInput,
  type RunHangWatchdogHandle,
} from "./runHangWatchdog";
import { watchWorkerLoad, type WorkerLoadWatch } from "./workerLoadWatch";

export type { RunCheckpoint, RunHangReportInput } from "./runHangWatchdog";

/** Host callback invoked per watchdog firing. Throws are swallowed by the watchdog. */
export type RunHangReporter = (report: RunHangReportInput) => void;


function makePythonWorker() {
  return new PythonWorker();
}

function makeCanvasWorker() {
  return new CanvasWorker();
}

enum LangId {
  PYTHON = "python",
  JAVASCRIPT = "javascript",
  JAVA = "java",
  CPLUSPLUS = "cpp",
  HTML = "html",
  CSS = "css",
}

export interface onLoadResponse {
    success: boolean;
    error?: string;
    /**
     * Set on a failure where the worker only ran out of time; it may still
     * report `success: true` later. Absent for every other outcome.
     */
    isTimeout?: boolean;
}


const CANVAS_ID_CACHE = new Set<string>();

// Cache for storing canvasId -> {offscreenCanvas, htmlCanvas} mappings
const CANVAS_CACHE = new Map<string, {
  htmlCanvas: HTMLCanvasElement;
  transferred: boolean;
}>();


export interface StandardIO {
  stdin: () => Promise<string>;
  stdout: (s: string) => void;
  stderr: (s: string) => void;
  onStep: (data: StepData) => void;
}

/**
 * Async provider that returns an opaque auth token string to attach to
 * a `runCode` dispatch, or `null` when no token is available (e.g.
 * unauthenticated session). The token is forwarded into `cacheState`
 * under the runtime-reserved key `idToken`, where Pyodide libraries
 * (`call_gpt`) read it via `get_static_data("idToken")` and attach it
 * as `Authorization: Bearer <token>` on outbound HTTP calls.
 *
 * Only runs that set `RunCodeOptions.attachAuthToken` get a token, because
 * the code being run can read it. The provider is invoked once per such
 * `runCode` call so callers (typically
 * the Firebase auth-aware host shell) can hand back a freshly-refreshed
 * token without this package needing to know how it's obtained. Throws
 * are caught by {@link composeRunCacheState} and treated as `null` so a
 * token-fetch hiccup degrades to an unauthenticated request rather than
 * aborting the run.
 */
export type GetAuthToken = () => Promise<string | null>;

/**
 * Composes the per-run wire cacheState from the caller's contribution
 * and any runtime-injected fields. Pure and synchronous-modulo-await:
 * does not touch class state and never mutates the input.
 *
 * The seam exists because `cacheState` is conceptually two things — a
 * caller-owned input describing the run's context (`projectId`,
 * `karelStartState`, ...) and the wire payload the worker installs as
 * `apiCache`. WorkerApi owns the wire payload; the caller owns its
 * input. This helper is the boundary.
 *
 * @param baseCacheState The caller-supplied cacheState
 *   (`options.cacheState`). May be `undefined`. Treated as read-only;
 *   the returned object is always a fresh shallow copy even when no
 *   runtime fields are added.
 * @param getAuthToken Optional async provider for the runtime-reserved
 *   `idToken` field. When omitted or `null`, no auth field is added.
 *   When provided and `attachAuthToken` is true, invoked once. Throws or
 *   non-string returns are logged at debug level and degrade to "no
 *   token" rather than propagating.
 * @param attachAuthToken Whether this run may carry the token (see
 *   `RunCodeOptions.attachAuthToken`). When omitted or false, the
 *   provider is not called and no auth field is added.
 * @return A fresh cacheState object suitable for posting on the RUN
 *   message. Never the caller's reference.
 * @throws Never; provider failures degrade to "no token".
 */
export async function composeRunCacheState({
  baseCacheState,
  getAuthToken,
  attachAuthToken = false,
  functionsBaseUrl,
}: {
  baseCacheState: CacheState | undefined;
  getAuthToken: GetAuthToken | null;
  attachAuthToken?: boolean;
  functionsBaseUrl: string | null;
}): Promise<CacheState> {
  const composed: CacheState = {...(baseCacheState ?? {})};
  // Strip any caller-set runtime-reserved keys defensively. These are
  // marked `@internal` on {@link CacheState} but TypeScript can't
  // enforce that at runtime, and we don't want a buggy or malicious
  // caller to forge an identity claim or redirect Pyodide's outbound
  // HTTP through this rail.
  delete composed.idToken;
  delete composed.functionsBaseUrl;

  if (functionsBaseUrl) {
    composed.functionsBaseUrl = functionsBaseUrl;
  }

  if (!getAuthToken || !attachAuthToken) {
    return composed;
  }
  try {
    const token = await getAuthToken();
    if (typeof token === 'string' && token.length > 0) {
      composed.idToken = token;
    }
  } catch (error) {
    console.debug(
      '[WorkerApi] getAuthToken threw; treating run as unauthenticated',
      error,
    );
  }
  return composed;
}

/**
 * Builds the {@link AdvancedAutograderState} sentinel returned for
 * runs that the advanced autograder cancelled (`stopRequested: true`)
 * before producing real grading data. Three call sites in
 * `_runCodeImpl` need this exact shape — pre-RUN cancel paths and the
 * post-RUN interrupt branch — so it lives here once instead of three
 * inline copies.
 *
 * TODO: [#2658](https://github.com/CodeinPlace/cip/issues/2658) — the
 * `Map<string, any>` shape on `AdvancedAutograderState` is not
 * genuinely unknown: the consumer in
 * `src/apps/webapp/src/assignments/unitTest/advancedConsole/advancedConsoleUtils.ts`
 * has the precise types (`AdvancedAutograderTrace`, `RandomVars`,
 * `ErrorState`) and even casts the autograde trace on read. The right
 * fix is to mirror those types from the webapp into this package's
 * `AdvancedAutograderState` interface so the `any` goes away
 * end-to-end. Until that lands, this helper localises the suppression
 * to one site rather than three duplicate copies; it does NOT
 * eliminate the underlying type lie.
 *
 * @return A fresh sentinel state with empty registered vars, an
 *   `errorState` carrying a single `has_error: false` entry, an empty
 *   autograde trace, and `stopRequested: true`.
 */
function makeInterruptedAutograderState(): AdvancedAutograderState {
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#2658](https://github.com/CodeinPlace/cip/issues/2658) - mirror webapp's RandomVars type instead of `any`
    registeredVars: new Map<string, any>(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#2658](https://github.com/CodeinPlace/cip/issues/2658) - mirror webapp's ErrorState type instead of `any`
    errorState: new Map<string, any>([['has_error', false]]),
    autogradeTrace: [],
    stopRequested: true,
  };
}

const LangIdToWorker: Partial<Record<LangId, () => Worker>> = {
  [LangId.PYTHON]: () => makePythonWorker(),
};




let sharedGraphicsWorker: Worker | null = null;

function getSharedGraphicsWorker(): Worker | null {
  if (!sharedGraphicsWorker) {
    // sharedGraphicsWorker = new CanvasWorker({ name: "graphics-worker" });
    sharedGraphicsWorker = makeCanvasWorker();
  }
  return sharedGraphicsWorker;
}


export function destroySharedGraphicsWorker() {
  if (sharedGraphicsWorker) {
    sharedGraphicsWorker.terminate();
    sharedGraphicsWorker = null;
  }
}

/**
 * Lifecycle states for a {@link RunRecord}. Phase 1 of #2453 only assigns
 * `'queued'` and `'running'` (records are removed from `runs` when they
 * finish, so `'cancelled'` and `'finished'` are not externally observable
 * yet); the type lists all four for forward compatibility with phases 2-3,
 * which surface a sanitized snapshot view and a public `cancel()` API.
 */
type RunState = 'queued' | 'running' | 'cancelled' | 'finished';

/**
 * Internal record for one `runCode` call. Owns the per-run callback
 * snapshots so dispatch is decoupled from `apiCallbacks` /
 * `socketCallbacks` mutations between queue-time and run-time, and a
 * `done` promise the next-queued record awaits before transitioning
 * `queued` → `running`.
 */
interface RunRecord {
  /** Short opaque id for logs and forward-compat with phase 4 telemetry. */
  runId: string;
  /** Lifecycle state. `'queued'` on creation, `'running'` once dequeued. */
  state: RunState;
  /** Wall-clock ms epoch when `runCode` was called. */
  enqueuedAt: number;
  /** Wall-clock ms epoch when the run actually entered `_runCodeImpl`. */
  startedAt?: number;
  /** Most recent {@link RunCheckpoint}. Initialised to `'ENQUEUED'`; advanced by `_runCodeImpl` and message handlers. */
  lastCheckpoint: RunCheckpoint;
  /** Watchdog handle, or `null` when no host reporter is wired in. */
  hangWatchdog: RunHangWatchdogHandle | null;
  /**
   * Per-run snapshot of `apiCallbacks` merged with any
   * `options.apiOverrides`. Used by `handleApiCall` for dispatch.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  apis: Record<string, (func: any, ...args: any[]) => CiPLibraryCallbackResponse>;
  /**
   * Per-run snapshot of `socketCallbacks` merged with any
   * `options.ioOverrides`. Used by the worker message handler for
   * stdin/stdout/stderr/onStep routing and by `_runCodeImpl` for
   * `onRunEnd`.
   */
  sockets: SockCallbacks;
  /**
   * Resolves when the run finishes (success, error, or interrupt). The
   * next-queued record awaits this promise before transitioning to
   * `'running'`.
   */
  done: Promise<void>;
  /** Resolves {@link done}; called from `runCode`'s finally block. */
  resolveDone: () => void;
  /**
   * Set by `interruptCode` when interrupt() is called against this record
   * before `_runCodeImpl` has assigned `runPromise` (i.e. while still
   * awaiting api re-registration on a freshly-respawned worker). The flag
   * is checked after that await; when set, `_runCodeImpl` resolves with
   * `{ interrupted: true }` instead of posting RUN. Without this, the
   * interrupt() call disappears into the gap between `runCode` entering
   * and `runPromise` being assigned, and the run proceeds untouched.
   */
  preRunCancelled?: boolean;
}

/**
 * How long `interruptCode` waits for the worker to hand back its accumulated
 * replay data before killing the worker anyway.
 *
 * The step-mode branch asks the worker for its trace data so a Stop doesn't
 * silently destroy the learner's replay. That request can only be answered
 * cooperatively, from inside Python, so anything that blocks the worker
 * thread without polling for interrupts leaves the await unsettled — and
 * before this deadline existed, unsettled meant forever: the Stop button
 * stayed stuck, `isBusy()` never cleared, and the program kept running.
 *
 * 8s is chosen to sit between the two timescales that matter. A cooperative
 * interrupt normally lands within one `call_gpt` round trip (~1s), so the
 * common case still wins the race and keeps its replay. A single hung
 * request is bounded by the server's own 60s timeout, so without a deadline
 * well under that the button would appear dead for a minute.
 *
 * Losing the replay for one interrupted run is the deliberate trade against
 * a permanently wedged tab.
 */
const INTERRUPT_STEP_DATA_TIMEOUT_MS = 8000;

/**
 * How long a new Python worker has to report PYODIDE_READY or
 * PYODIDE_LOAD_ERROR before the load counts as failed. A cold load fetches
 * several megabytes from the Pyodide CDN, so this is sized for a slow
 * connection. A worker that reports ready after it still recovers: the
 * success callback fires as usual.
 */
const WORKER_LOAD_TIMEOUT_MS = 120_000;

/**
 * Awaits the worker's interrupt step data, giving up after `timeoutMs`.
 *
 * @param stepDataPromise Settles when the worker posts its replay data.
 * @param timeoutMs Deadline in milliseconds.
 * @return The worker's step data, or `undefined` if the deadline won.
 *
 * Exported for tests: `interruptCode` can't be exercised in a unit test
 * without a live Worker, sync channel and pyodide load, so the deadline
 * semantics are covered here instead.
 *
 * The timer is always cleared, including on the cooperative path — a
 * resolved interrupt shouldn't leave a pending timer alive for the rest of
 * the deadline.
 */
export async function awaitStepDataWithDeadline<T>(
  stepDataPromise: Promise<T>,
  timeoutMs: number,
): Promise<T | undefined> {
  let deadline: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      stepDataPromise,
      new Promise<undefined>(resolve => {
        deadline = setTimeout(() => resolve(undefined), timeoutMs);
      }),
    ]);
  } finally {
    if (deadline !== undefined) {
      clearTimeout(deadline);
    }
  }
}

export class WorkerApi {
  private worker: Worker | null;
  private graphicsWorker: Worker | null = null;
  private socketCallbacks: SockCallbacks;
  private runPromise: AwaitablePromise;
  private canvas: HTMLElement | null = null;
  private controlEventsView: Map<number, number> = new Map();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  private apiCallbacks: Record<string, (func: any, ...args: any[]) => CiPLibraryCallbackResponse> = {};
  /**
   * Ordered list of in-flight + queued `runCode` calls on this plug. The
   * head (when present) is currently dispatching; entries past the head
   * are queued, blocked on their predecessor's `done` promise.
   *
   * Replaces the previous `runQueue: Promise<unknown>` + `activeRunCount:
   * number` pair. Multiple consumers sharing a `BasePlug` (e.g. sibling
   * `RunnableKarel` blocks embedded in the same TipTap doc) line up here
   * in call order so their worker responses, snapshots, and FINISHED
   * messages stay routed to the right caller.
   *
   * Records drop from `runs` when their `runCode` finally block runs.
   * Phase 1 of #2453 drops them immediately on completion; later phases
   * will retain finished/cancelled records for a final `onRunsChange`
   * emission.
   */
  private runs: RunRecord[] = [];
  /**
   * Pointer to the record currently inside `_runCodeImpl`, or `null` when
   * no run is dispatching. Equals `runs[0]` while set; tracked separately
   * so dispatch sites (`handleApiCall`, the worker message handler) can
   * route through the active record without scanning `runs`.
   */
  private activeRecord: RunRecord | null = null;
  /** Monotonic counter feeding `RunRecord.runId`. */
  private nextRunSequence: number = 0;
  private runStatusListeners: Set<(busy: boolean) => void> = new Set();
  /**
   * Hooks invoked synchronously immediately before each run posts RUN to
   * the worker — after the queue wait and before the worker reads any
   * control bytes for the run. Used by library-specific concerns
   * (notably `BasePlug.setKarelSleepTime`) to re-assert control bytes
   * that `controlEventsView.clear()` wipes on FINISHED, without
   * forcing every `runCode` consumer to remember to re-write them.
   * Hooks run in insertion order; thrown errors are caught and logged
   * so one misbehaving subscriber cannot block a run.
   */
  private preRunHooks: Set<() => void> = new Set();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  private apiOnStep: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  private apiBuffer: CappedMap<string, any> = new CappedMap<string, any>(2000);
  private pyodideReady: boolean = false;
  /** Watches the current worker until it reports its load outcome. */
  private loadWatch: WorkerLoadWatch | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  private messageBacklog: any[] = [];
  private librariesCache: Map<string, CiPLibrary> = new Map();
  private traceDataLength: number = 0;
  private onLoadCallback: (data: onLoadResponse) => void;
  /**
   * Optional provider that returns a Bearer token (or `null`) to merge
   * into the `cacheState.idToken` of runs that set `attachAuthToken`. Set
   * via constructor. Invoked inside `_runCodeImpl` just before the RUN
   * postMessage. See
   * {@link GetAuthToken} for semantics.
   */
  private getAuthToken: GetAuthToken | null;
  /**
   * Optional Cloud Functions base URL (no trailing slash, no function
   * name) to merge into each run's `cacheState.functionsBaseUrl`. Set
   * via constructor; stable for the worker's lifetime. Pyodide
   * libraries (e.g. `call_gpt`) read it via
   * `get_static_data("functionsBaseUrl")` and append the function
   * name. Pass the production URL in normal mode and the emulator URL
   * in test/dev mode so the same wheel works against both. `null`
   * means "Pyodide should fall back to its built-in default" (which
   * is the production URL).
   */
  private functionsBaseUrl: string | null;
  /** Host hang-beacon callback; `null` disables the watchdog. */
  private onRunHang: RunHangReporter | null;
  /** Prior run's final checkpoint, threaded into the next run's beacon. */
  private previousRunOutcome: RunCheckpoint | null = null;
  private apiRegistrationPromises: Map<string, AwaitablePromise> = new Map();
  private apiReRegistrationPromise: AwaitablePromise | null = null;
  // Service worker channel for synchronous communication with worker
  // Use absolute URL to match worker's channel (workers use blob URLs where relative paths fail)
  private swChannel: Channel = makeServiceWorkerChannel({ scope: window.location.origin + "/", timeout: 60000 });
  private stepMode: boolean = false;
  private stepDataOnInterruptPromise : AwaitablePromise | null = null;
  private awaitingStdin: boolean = false;
  private pendingStdinMessageId: string | null = null;

  constructor(
    langId: LangId,
    standardIO: StandardIO,
    // interruptBuffer: Uint8Array,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    onLoadCallback: (data: onLoadResponse) => void = (data: any) => {},
    getAuthToken: GetAuthToken | null = null,
    functionsBaseUrl: string | null = null,
    onRunHang: RunHangReporter | null = null,
  ) {
    this.worker = null;
    this.socketCallbacks = standardIO;
    // this.interruptBuffer = interruptBuffer;
    // this.pauseBuffer = new Int32Array(new SharedArrayBuffer(4));
    this.onLoadCallback = onLoadCallback;
    this.getAuthToken = getAuthToken;
    this.functionsBaseUrl = functionsBaseUrl;
    this.onRunHang = onRunHang;


    this.initializeWorker(langId);
  }


  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  initializeWorker(langId: LangId, preloadedStepData?: { traceList?: any[]; outputList?: string[] }) {
    if (!this.worker) {
      this.loadWatch?.settle();
      this.worker = LangIdToWorker[langId]();
      this.worker.addEventListener("message", this.handleMessageFromWorker);
      this.loadWatch = watchWorkerLoad({
        worker: this.worker,
        timeoutMs: WORKER_LOAD_TIMEOUT_MS,
        onFailure: ({ reason, isTimeout }) =>
          this.onLoadCallback({ success: false, error: reason, isTimeout }),
      });
      this.graphicsWorker = getSharedGraphicsWorker();
      const libraries = Array.from(this.librariesCache.values()).map(lib => ({ id: lib.id, srcPath: lib.srcPath }));
      this.worker.postMessage({
        type: MainToWorkerEvent.SETUP,
        channel: this.swChannel,
        preloadedStepData,
        libraries
      });
      // send interrupt buffer for keyboard interrupt
    }
  }

  async runCode(code: string, options?: RunCodeOptions): Promise<RunResult> {
    const apiOverrides = options?.apiOverrides || {};
    const ioOverrides = options?.ioOverrides || {};

    // Snapshot api + socket callbacks synchronously at queue time. Holding
    // the snapshot on the record means later `registerApi` /
    // `registerCallbacks` calls cannot reach back into queued or in-flight
    // runs — only runs queued after them see the new registrations.
    const apis = {...this.apiCallbacks, ...apiOverrides};
    const sockets: SockCallbacks = {
      ...this.socketCallbacks,
      ...(ioOverrides.input ? {stdin: ioOverrides.input} : {}),
      ...(ioOverrides.output ? {stdout: ioOverrides.output} : {}),
      ...(ioOverrides.error ? {stderr: ioOverrides.error} : {}),
      ...(ioOverrides.onRunEnd ? {onRunEnd: ioOverrides.onRunEnd} : {}),
      ...(ioOverrides.onStep ? {onStep: ioOverrides.onStep} : {}),
    };

    this.nextRunSequence++;
    let resolveDone: () => void = () => {};
    const done = new Promise<void>(resolve => {
      resolveDone = resolve;
    });
    const record: RunRecord = {
      runId: `run-${this.nextRunSequence.toString(36)}`,
      state: 'queued',
      enqueuedAt: Date.now(),
      apis,
      sockets,
      done,
      resolveDone,
      lastCheckpoint: 'ENQUEUED',
      hangWatchdog: null,
    };

    // Read the predecessor before pushing self, so we don't await our own
    // `done` promise.
    const previousRecord = this.runs[this.runs.length - 1];
    const wasBusy = this.runs.length > 0;
    this.runs.push(record);
    if (!wasBusy) {
      this._emitRunStatus(true);
    }

    if (previousRecord) {
      await previousRecord.done;
    }

    record.state = 'running';
    record.startedAt = Date.now();
    this.activeRecord = record;
    if (this.onRunHang) {
      record.hangWatchdog = scheduleHangWatchdog({
        input: {
          runId: record.runId,
          initialCheckpoint: record.lastCheckpoint,
          testMode: options?.testMode ?? false,
          stepMode: options?.stepMode ?? false,
        },
        onTimeout: report => {
          this.onRunHang?.(report);
        },
        getPreviousRunOutcome: () => this.previousRunOutcome,
        getStdinState: () => ({
          awaitingStdin: this.awaitingStdin,
          pendingStdinMessageId: this.pendingStdinMessageId,
        }),
      });
    }
    try {
      return await this._runCodeImpl(code, options, record);
    } finally {
      record.hangWatchdog?.cancel();
      this.previousRunOutcome = record.lastCheckpoint;
      this.activeRecord = null;
      const index = this.runs.indexOf(record);
      if (index !== -1) {
        this.runs.splice(index, 1);
      }
      record.resolveDone();
      if (this.runs.length === 0) {
        this._emitRunStatus(false);
      }
    }
  }

  /**
   * Advance the record's checkpoint and forward to its hang watchdog.
   *
   * @param record Record being advanced (typically `this.activeRecord`).
   * @param next New checkpoint. No-op when equal to the current value.
   */
  private _advanceCheckpoint(record: RunRecord, next: RunCheckpoint): void {
    if (record.lastCheckpoint === next) {
      return;
    }
    record.lastCheckpoint = next;
    record.hangWatchdog?.checkpoint(next);
  }

  /**
   * Subscribe to plug-busy transitions. The listener fires with `true` on
   * the first `runCode` call and `false` when the last in-flight/queued run
   * completes. Returns an unsubscribe handle.
   *
   * @param listener - Called with the current busy state on every transition.
   * @return Function that removes the listener when invoked.
   */
  onRunStatusChange(listener: (busy: boolean) => void): () => void {
    this.runStatusListeners.add(listener);
    return () => {
      this.runStatusListeners.delete(listener);
    };
  }

  /**
   * Whether any `runCode` call is in flight or queued on this plug or there is
   * a current runPromise pending execution or interruption.
   */
  isBusy(): boolean {
    return this.runs.length > 0;
  }

  /**
   * Register a hook to fire synchronously just before each run posts RUN
   * to the worker. Hooks run after the queue wait completes (so
   * back-to-back queued runs each see their own hook invocation) and
   * before the worker has had a chance to read any control bytes for the
   * run.
   *
   * @param hook - Synchronous callback invoked once per run dispatch.
   * @return Unsubscribe function; invoke to stop receiving invocations.
   */
  addPreRunHook(hook: () => void): () => void {
    this.preRunHooks.add(hook);
    return () => {
      this.preRunHooks.delete(hook);
    };
  }

  private _firePreRunHooks(): void {
    for (const hook of this.preRunHooks) {
      try {
        hook();
      } catch (error) {
        console.debug('[WorkerApi] pre-run hook threw', error);
      }
    }
  }

  private _emitRunStatus(busy: boolean): void {
    for (const listener of this.runStatusListeners) {
      try {
        listener(busy);
      } catch (error) {
        console.debug('[WorkerApi] run-status listener threw', error);
      }
    }
  }

  private async _runCodeImpl(
    code: string,
    options: RunCodeOptions | undefined,
    record: RunRecord,
  ): Promise<RunResult> {
    if (!this.worker) {
      throw new Error("Worker not initialized");
    }
    // After an interrupt, the worker has been replaced and previously cached
    // APIs are being re-registered via messageBacklog. Wait for those
    // registrations to confirm before posting RUN — otherwise the worker
    // can receive RUN before the REGISTER_API messages from the backlog
    // and run without any API endpoints registered (which manifests as
    // instant runs with no per-step events).
    if (this.apiRegistrationPromises.size > 0) {
      this._advanceCheckpoint(record, 'API_REG_AWAITING');
      await Promise.all(
        [...this.apiRegistrationPromises.values()].map(p => p.promise),
      );
    }
    this._advanceCheckpoint(record, 'API_REG_DONE');
    // Honor an interrupt() that landed during the api-registration await,
    // when `runPromise` was still null and `interruptCode` couldn't take
    // its normal path. Bail before posting RUN so the run resolves as
    // interrupted with no work performed on the worker. Mirror the
    // advancedAutograderState shape that the regular interrupt branch
    // (post-RUN, see below) returns when `testMode === 'advancedAutograder'`,
    // so autograder callers can still detect the cancel via `stopRequested`.
    if (record.preRunCancelled) {
      const testMode = options?.testMode || false;
      const interruptedResult: RunResult = {
        error: [],
        output: [],
        traceData: [],
        fromTests: false,
        advancedAutograderState:
          testMode === 'advancedAutograder'
            ? makeInterruptedAutograderState()
            : undefined,
      };
      record.sockets.onRunEnd?.(interruptedResult);
      return interruptedResult;
    }
    this.stepMode = options?.stepMode || false;
    // Invariant: apiBuffer is owned by the most recent step-mode run on
    // this plug. At the start of a new step-mode run, drop the prior
    // run's entries so _stepApiCall can't return them.
    //
    // Without this clear, _stepApiCall's "fall back to the earliest entry
    // when the current step's sign isn't in the buffer" branch will hand
    // a previous program's API snapshot to onStep — e.g. scrubbing the
    // first few steps of one Karel program would render the previous
    // Karel program's world.
    //
    // The clear is gated on `this.stepMode` because handleApiCall only
    // writes to apiBuffer when stepMode is true. A single user-initiated
    // IDE "Run" doesn't end at the main run — it fans out into several
    // non-step runCode calls (e.g. the unit-test phase that follows a
    // Karel run, REPL commands). Those non-step runs don't repopulate
    // the buffer, so an unconditional clear here would wipe the main
    // run's data the moment the test phase started and the replay slider
    // would have nothing to render. Scoping the clear to step-mode runs
    // keeps the buffer owned by the run the user is actually scrubbing.
    if (this.stepMode) {
      this.apiBuffer.clear();
    }
    const testMode = options?.testMode || false;
    const replMode = options?.replMode || false;
    const gradingScripts = options?.gradingScripts || null;
    // Compose the wire cacheState from caller input + runtime-injected
    // fields. The caller's `options.cacheState` is treated as
    // read-only; the returned object is a fresh shallow copy and is
    // never the caller's reference. See {@link composeRunCacheState}.
    this._advanceCheckpoint(record, 'AUTH_TOKEN_AWAITING');
    const cacheState = await composeRunCacheState({
      baseCacheState: options?.cacheState,
      getAuthToken: this.getAuthToken,
      attachAuthToken: options?.attachAuthToken,
      functionsBaseUrl: this.functionsBaseUrl,
    });
    this._advanceCheckpoint(record, 'AUTH_TOKEN_DONE');
    // Honor an interrupt() that landed during the auth-token await.
    // Mirrors the re-check after the api-registration await above; the
    // worker is intact (we haven't posted RUN), so we just resolve as
    // interrupted with no work performed.
    if (record.preRunCancelled) {
      const interruptedResult: RunResult = {
        error: [],
        output: [],
        traceData: [],
        fromTests: false,
        advancedAutograderState:
          testMode === 'advancedAutograder'
            ? makeInterruptedAutograderState()
            : undefined,
      };
      record.sockets.onRunEnd?.(interruptedResult);
      return interruptedResult;
    }
    const files = options?.files || [];
    // this.interruptBuffer[0] = 0;
    const imageCache = options?.imageCache || {};

    this.graphicsWorker?.postMessage({
      type: MainToCanvasEvent.IMAGE_CACHE,
      imageCache: imageCache
    });

    // wrap in promise so caller can await completion
    this.runPromise = getAwaitablePromise();

    // Drop any clicks/keys/mouse bytes that landed between the prior
    // FINISHED and this run's dispatch. The canvas listeners stay bound
    // across runs, so an idle-time click on the canvas would otherwise
    // leak into this run's first wait_for_click(). FINISHED clears at
    // end-of-run; this is the symmetric guard for events that arrive
    // *between* runs. Must run BEFORE _firePreRunHooks() — the karel
    // sleep-binding hook writes a byte into controlEventsView that the
    // worker reads on its first time.sleep(), and clearing after the
    // hook would wipe that byte and regress the share-page sleep bug
    // covered by the karel-sleep-byte-persists test.
    this.controlEventsView.clear();

    // Fire synchronously after the queue wait and just before posting
    // RUN. The worker reads `controlEventsView` for the first time during
    // execution, so hooks that write into it (e.g. the karel sleep
    // binding's byte) must run here — earlier (at enqueue time) would
    // let a back-to-back run wipe the byte via FINISHED before this
    // run starts.
    this._firePreRunHooks();

    this.worker.postMessage({
      type: MainToWorkerEvent.RUN,
      // buffer: this.sharedArrayBuffer,
      code,
      stepMode: this.stepMode,
      testMode,
      testLineCount: options?.testLineCount,
      replMode,
      cacheState,
      files,
      gradingScripts,
      randomSeed: options?.randomSeed,
    });
    this._advanceCheckpoint(record, 'RUN_POSTED');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    const data = await this.runPromise.promise as any;

    let traceData = data.traceList || [];
    const fromTests = data.fromTests || false;
    traceData = this._addApiDataToTrace(traceData);
    const outputList = data.outputList || [];

    const wasInterrupted = Boolean(data?.interrupted);
    const advancedAutograderState =
      wasInterrupted && testMode === 'advancedAutograder'
        ? makeInterruptedAutograderState()
        : data.advancedAutograderState || undefined;

    const runResult: RunResult = {
      error: data.errorList || [],
      output: outputList,
      traceData: traceData,
      fromTests,
      advancedAutograderState,
      interrupted: wasInterrupted,
    }
    // Route onRunEnd through the per-run snapshot so ioOverrides.onRunEnd
    // still fires even if `socketCallbacks` was swapped during the run.
    record.sockets.onRunEnd?.(runResult);
    return runResult;
  }

  async interruptCode() {
    let finishedData;
    // No run in flight → nothing to interrupt. Bail out before doing the
    // worker terminate/respawn dance — that path costs a fresh pyodide
    // load the next run would otherwise avoid, and (with stepMode set
    // from the prior completed run) the await on
    // stepDataOnInterruptPromise would hang forever because the idle
    // worker has no trace data to flush.
    // `runPromise` is reset to null on FINISHED, so a non-null value
    // means a `_runCodeImpl` invocation is still awaiting a result.
    if (this.runPromise === null) {
      // Edge case: the active record has entered `_runCodeImpl` but is
      // still awaiting api re-registration (RUN hasn't been posted yet,
      // so `runPromise` isn't set). Without recording the cancel against
      // the record, the interrupt would silently disappear into that
      // gap and the run would start as if interrupt() was never called.
      // Mark the record so `_runCodeImpl` returns `{ interrupted: true }`
      // before posting RUN. The worker is left intact — it's already
      // mid-load and the queued REGISTER_API messages should still flush
      // for the next run.
      if (this.activeRecord) {
        this.activeRecord.preRunCancelled = true;
      }
      return;
    }
    if (this.awaitingStdin) {
      (this.activeRecord?.sockets.stdout ?? this.socketCallbacks.stdout)("\n");
    }
    if (this.stepMode) {
      this.stepDataOnInterruptPromise = getAwaitablePromise();
      if (this.awaitingStdin && this.pendingStdinMessageId) {
        // Unblock the worker's synchronous stdin wait so it can post step data.
        const stdinId = this.pendingStdinMessageId;
        this.pendingStdinMessageId = null;
        writeMessage(this.swChannel, { interrupted: true }, stdinId);
      }
      // Race the cooperative reply against a deadline. Only the worker can
      // settle this promise, and it can only do so from Python, so any
      // blocking call that doesn't poll for interrupts would otherwise
      // strand this await and never reach the terminate() below.
      // `undefined` on expiry is safe downstream: `initializeWorker` takes
      // `preloadedStepData` as optional, and `{...undefined}` spreads to
      // nothing in the runPromise resolution.
      finishedData = await awaitStepDataWithDeadline(
        this.stepDataOnInterruptPromise.promise,
        INTERRUPT_STEP_DATA_TIMEOUT_MS,
      );
      if (finishedData === undefined) {
        console.warn(
          `[WorkerApi] interrupt: worker did not return step data within ` +
            `${INTERRUPT_STEP_DATA_TIMEOUT_MS}ms; terminating without replay data.`,
        );
      }
      // destroy promise so that future interrupts don't accidentally use old promise
      this.stepDataOnInterruptPromise = null;
    }
    this.awaitingStdin = false;
    this.pendingStdinMessageId = null;
    // Kill worker, spin up a new one
    this.worker?.removeEventListener("message", this.handleMessageFromWorker);
    this.worker?.terminate();
    this.worker = null;
    this.pyodideReady = false;
    this.initializeWorker(LangId.PYTHON, finishedData);
    // Re-register all previously cached APIs with the new worker. Without
    // this, post-interrupt runs return immediately and skip per-step events
    // because the new worker has no API endpoints registered. The async call
    // queues REGISTER_API messages into messageBacklog (since pyodideReady
    // is false here); they're flushed when the new worker reports ready.
    // The promise it returns resolves once the worker confirms registration
    // — `_runCodeImpl` awaits the same per-API promises before posting RUN,
    // so the ordering is guaranteed without explicitly awaiting here.
    void this._reRegisterApiAfterLoad();
    if (this.runPromise) {
      this.runPromise.resolve({ interrupted: true,
        ...finishedData
       });
      // Mirror the FINISHED-handler bookkeeping. Without clearing this,
      // a subsequent interrupt() call would see a non-null runPromise and
      // re-enter the stepMode branch, awaiting stepDataOnInterruptPromise
      // against an idle (post-interrupt) worker that will never resolve it.
      this.runPromise = null;
    }
  }


  /**
   * Replaces the worker with a fresh one and loads Pyodide again, keeping the
   * registered APIs. For recovering from a failed load: the outcome arrives
   * through `onLoadCallback` like the first load. A run waiting on the failed
   * worker resolves as interrupted, so it cannot hold the run queue; runs
   * queued behind it wait for the new worker.
   *
   * @return Nothing; the load outcome is reported through `onLoadCallback`.
   * @throws Never.
   */
  restartWorker(): void {
    // Re-registration below replaces these promises, so a run awaiting the
    // old ones would otherwise never resolve.
    if (this.activeRecord) {
      this.activeRecord.preRunCancelled = true;
    }
    for (const pending of this.apiRegistrationPromises.values()) {
      pending.resolve();
    }
    this.apiRegistrationPromises.clear();
    if (this.runPromise) {
      this.runPromise.resolve({ interrupted: true });
      this.runPromise = null;
    }
    this.awaitingStdin = false;
    this.pendingStdinMessageId = null;
    this.worker?.removeEventListener("message", this.handleMessageFromWorker);
    this.worker?.terminate();
    this.worker = null;
    this.pyodideReady = false;
    // Messages queued for the failed worker; re-registration queues the APIs again.
    this.messageBacklog = [];
    this.initializeWorker(LangId.PYTHON);
    void this._reRegisterApiAfterLoad();
  }

  setStep(length: number) {
    const msg = {
      type: MainToWorkerEvent.SET_TRACE_STEP,
      length: length,
    };
    // Route through messageBacklog when the worker isn't ready yet (e.g.
    // immediately after interruptCode replaces the worker). The worker's
    // SET_TRACE_STEP handler doesn't `await pyodideInitialized` and uses
    // `self.graphicsChannel?.postMessage(...)` with optional chaining, so
    // a direct post arriving pre-PYODIDE_READY would silently no-op the
    // graphics canvas update for that step (graphicsChannel isn't set
    // until PORT, which is sent in main's PYODIDE_READY handler).
    // Backlog flushes immediately after sendBuffers() in PYODIDE_READY,
    // so order on the worker becomes PORT -> REGISTER_API -> SET_TRACE_STEP
    // and graphics lands.
    if (!this.pyodideReady) {
      this.messageBacklog.push(msg);
      return;
    }
    this.worker?.postMessage(msg);
  }

  clearReplState() {
    // Drop stale clicks/keys/mouse snapshot on the main thread; worker reset
    this.controlEventsView.clear();
    this.worker?.postMessage({
      type: MainToWorkerEvent.CLEAR_REPL_STATE
    });
  }


  handleMessageFromWorker = (event: MessageEvent) => {
    if (!this.worker) {
      throw new Error("Worker not initialized");
    }

    const type = event.data.type;
    switch (type) {
      case WorkerToMainEvent.PYODIDE_READY:
        this.loadWatch?.settle();
        this.pyodideReady = true;
        this.sendBuffers();
        // flush backlog
        this.messageBacklog.forEach(msg => {
          this.worker?.postMessage(msg);
        });
        this.messageBacklog = [];
        this.onLoadCallback({
          success: true
        });
        break;
      case WorkerToMainEvent.PYODIDE_LOAD_ERROR:
        this.loadWatch?.settle();
        this.onLoadCallback({
          success: false,
          error: event.data.error,
        });
        break;
      case WorkerToMainEvent.STDIN:
        // Get the messageId from the worker's request
        const stdinMessageId = event.data.messageId;
        this.awaitingStdin = true;
        this.pendingStdinMessageId = stdinMessageId;
        if (this.activeRecord) {
          this._advanceCheckpoint(this.activeRecord, 'STDIN_REQUESTED');
        }
        (this.activeRecord?.sockets.stdin ?? this.socketCallbacks.stdin)().then(async (text: string) => {
          if (this.pendingStdinMessageId !== stdinMessageId) {
            return;
          }
          this.awaitingStdin = false;
          this.pendingStdinMessageId = null;
          if (this.activeRecord) {
            this._advanceCheckpoint(this.activeRecord, 'STDIN_RESPONDED');
          }
          await writeMessage(this.swChannel, { text }, stdinMessageId);
        });
        break;

      case WorkerToMainEvent.GET_IS_INTERRUPTED:
        const isInterruptedMessageId = event.data.messageId;
        const isInterrupted = this.stepDataOnInterruptPromise !== null;
        writeMessage(this.swChannel, { isInterrupted }, isInterruptedMessageId);
        break;
      case WorkerToMainEvent.SEND_INTERRUPT_STEP_DATA:
        // Received raw trace data from worker on interrupt
        // Need to request canvas history and zip them together
        this._handleInterruptStepData(event.data);
        break;
      case WorkerToMainEvent.STDOUT:
        (this.activeRecord?.sockets.stdout ?? this.socketCallbacks.stdout)(event.data.stdout);
        break;
      case WorkerToMainEvent.STDERR:
        (this.activeRecord?.sockets.stderr ?? this.socketCallbacks.stderr)(event.data.stderr);
        break;
      case WorkerToMainEvent.RUN_STARTED:
        this.bindCanvasListeners();
        break;
      case WorkerToMainEvent.FINISHED:
        if (event.data.interrupted && this.stepDataOnInterruptPromise) {
          // Stdin-interrupt path: worker finished its cleanup and sent the
          // preserved step data via FINISHED. Hand it to interruptCode().
          this.stepDataOnInterruptPromise.resolve({
            traceList: event.data.traceList || [],
            outputList: event.data.outputList || [],
            errorList: event.data.errorList || [],
            fromTests: false,
          });
          break;
        }
        if (!this.runPromise) {
          break;
        }
        if (this.activeRecord) {
          this._advanceCheckpoint(this.activeRecord, 'FINISHED');
        }
        this.runPromise.resolve(event.data);
        this.runPromise = null;
        this.stepDataOnInterruptPromise = null;
        this.controlEventsView.clear();
        break;
      case WorkerToMainEvent.API_CALL:
        const { module, func, args, stepMode, sign } = event.data;
        this.handleApiCall(sign, module, func, args, stepMode);
        break;
      case WorkerToMainEvent.INIT_CANVAS:
        if (this.canvas) {
          const { width, height } = event.data;
          // set canvas size
          (this.canvas as HTMLCanvasElement).width = width;
          (this.canvas as HTMLCanvasElement).height = height;
        }
        break;
      case WorkerToMainEvent.STEP_API:
        const data = event.data.data;
        this._stepApiCall(data[StepVariables.API_CALL_POINTER]);
        const onStepFunc = this.activeRecord?.sockets.onStep ?? this.socketCallbacks.onStep;
        if (onStepFunc) {
          onStepFunc({
            [StepVariables.LINENO]: data[StepVariables.LINENO],
            [StepVariables.SCOPE_NAME]: data[StepVariables.SCOPE_NAME],
            [StepVariables.LOCALS]: data[StepVariables.LOCALS],
            [StepVariables.OUTPUT]: data[StepVariables.OUTPUT],
          });
        }
        break;
        case WorkerToMainEvent.API_LOAD_FINISHED:
          const apiId = event.data.apiId;
          const registrationPromise = this.apiRegistrationPromises.get(apiId);
          if (registrationPromise) {
            registrationPromise.resolve();
          }
          break;
        case WorkerToMainEvent.GET_CONTROL_EVENTS:
          this._sendControlEvents(event.data.messageId, event.data.withDelete).catch((error) => {
            console.warn('[WorkerApi] Failed to send control events via sync channel', error);
          });
          break;
    }
  };



  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  handleApiCall = (sign: string, module: string, func: string, args: any, stepMode: boolean=false) => {
    // Prefer the active record's snapshot so late `registerApi` calls on
    // the shared plug cannot hijack an in-flight or queued run. Fall back
    // to the live registry for any out-of-run dispatch (shouldn't happen
    // in normal flow).
    const api = this.activeRecord?.apis[module] ?? this.apiCallbacks[module];
    if (api) {
      let data = api(func, ...args);
      if (!data ) {
        data = {
          apiId: module
        }
      } else {
        data = {
          ...data,
          apiId: module
        }
      }
      if (stepMode) {
        this.apiBuffer.set(sign, data);
      }
    }
    return
  }

  pause() {
    // Atomics.store(this.pauseBuffer, 0, 1);
  }

  resume() {
    // Atomics.store(this.pauseBuffer, 0, 0);
    // Atomics.notify(this.pauseBuffer, 0, 1);
  }

  sendCanvas(canvas: HTMLElement, canvasId: string) {
    const htmlCanvas = canvas as HTMLCanvasElement;

    // Check if this canvas has already been transferred
    const cached = CANVAS_CACHE.get(canvasId);
    if (cached?.transferred) {
      this._switchCanvas(canvasId);
      return;
    }

    // Store canvas reference and mark as transferred
    CANVAS_CACHE.set(canvasId, {
      htmlCanvas: htmlCanvas,
      transferred: true,
    });

    // Legacy cache for backward compatibility
    if (canvas?.id) {
      CANVAS_ID_CACHE.add(canvas.id);
    }

    this.canvas = canvas;

    // Transfer control to offscreen canvas
    const offscreen = htmlCanvas.transferControlToOffscreen();
    this.graphicsWorker?.postMessage(
      {
        type: MainToCanvasEvent.CANVAS,
        canvas: offscreen,
        canvasId: canvasId,
      },
      [offscreen],
    );
  }

  _switchCanvas(canvasId: string) {
    const cached = CANVAS_CACHE.get(canvasId);
    if (!cached) {
      console.warn("Canvas not found in cache:", canvasId);
      return;
    }

    this.canvas = cached.htmlCanvas;

    // Notify the graphics worker to switch to this canvas
    this.graphicsWorker?.postMessage({
      type: MainToCanvasEvent.SWITCH_CANVAS,
      canvasId: canvasId,
    });
  }



  bindCanvasListeners() {
    if (!this.canvas) {
      console.warn("No canvas to bind listeners to");
      return;
    };
    this.canvas.addEventListener("mousedown", (e) => {
      // update shared buffer
      // this.controlEventsView[0] = e.offsetX & 0xff;
      // this.controlEventsView[1] = (e.offsetX >> 8) & 0xff;
      // this.controlEventsView[2] = e.offsetY & 0xff;
      // this.controlEventsView[3] = (e.offsetY >> 8) & 0xff;
      this.controlEventsView.set(0, e.offsetX & 0xff);
      this.controlEventsView.set(1, (e.offsetX >> 8) & 0xff);
      this.controlEventsView.set(2, e.offsetY & 0xff);
      this.controlEventsView.set(3, (e.offsetY >> 8) & 0xff);
    });

    this.canvas.addEventListener("mousemove", (e) => {
      // update shared buffer
      // this.controlEventsView[4] = e.offsetX & 0xff;
      // this.controlEventsView[5] = (e.offsetX >> 8) & 0xff;
      // this.controlEventsView[6] = e.offsetY & 0xff;
      // this.controlEventsView[7] = (e.offsetY >> 8) & 0xff;
      this.controlEventsView.set(4, e.offsetX & 0xff);
      this.controlEventsView.set(5, (e.offsetX >> 8) & 0xff);
      this.controlEventsView.set(6, e.offsetY & 0xff);
      this.controlEventsView.set(7, (e.offsetY >> 8) & 0xff);
    });

    window.addEventListener("keydown", (e) => {
      const code = e.keyCode;
      // this.controlEventsView[8] = code & 0xff;
      // this.controlEventsView[9] = (code >> 8) & 0xff;
      this.controlEventsView.set(8, code & 0xff);
      this.controlEventsView.set(9, (code >> 8) & 0xff);
    });

  }


  setControlEventsBuffer(index: number, value: number) {
    if (index < 0) {
      return;
    }
    const index2 = index + 1;
    this.controlEventsView.set(index, value & 0xff);
    this.controlEventsView.set(index2, (value >> 8) & 0xff);
  }

  sendBuffers() {
    // this.worker.postMessage({
    //   type: MainToWorkerEvent.BUFFERS,
    //   interruptBuffer: this.interruptBuffer,
    //   controlEventsBuffer: this.controlEventsBuffer,
    //   pauseBuffer: this.pauseBuffer,
    // });

    const channel = new MessageChannel();
    // distribute ports
    this.worker.postMessage(
      {
        type: MainToWorkerEvent.PORT,
        port: channel.port1,
      }, [channel.port1]
    );
    this.graphicsWorker.postMessage(
      {
        type: MainToWorkerEvent.PORT,
        port: channel.port2,
      }, [channel.port2]
    );
  }


  registerCallbacks(callbacks: SockCallbacks) {
    this.socketCallbacks = {
            ...this.socketCallbacks,
            ...callbacks,
        };


  }

    async registerApi(apiRegistration: CiPLibrary, override: boolean = false) {
        const { id: apiId, srcPath: apiSrcPath, callback: apiCallback, onStep: apiOnStep } = apiRegistration;
        if (apiCallback) {
            this.apiCallbacks[apiId] = apiCallback;
        }
        if (apiOnStep) {
            this.apiOnStep[apiId] = apiOnStep;
        }
        if (this.librariesCache.has(apiId) && !override) {
          this.librariesCache.set(apiId, {
            id: apiId,
            ...this.librariesCache.get(apiId),
            ...apiRegistration,
          });
          return;
        }

        this.librariesCache.set(apiId, apiRegistration);
        // add a simple promise that can easily be awaited and resolved
        this.apiRegistrationPromises.set(apiId, getAwaitablePromise());
        if (!this.pyodideReady) {
          // backlog message - will be sent when pyodide is ready
          this.messageBacklog.push({
            type: MainToWorkerEvent.REGISTER_API,
            apiId,
            apiSrcPath
          });
          // Don't return early - still await the promise so re-registration
          // waits for the API to actually load after pyodide becomes ready
        } else {
          this.worker?.postMessage({
              type: MainToWorkerEvent.REGISTER_API,
              apiId,
              apiSrcPath
          });
        }
        await this.apiRegistrationPromises.get(apiId).promise;
    }

    private _stepApiCall(sign: string) {
      let apiStepData;
      let apiId;
      if (!this.apiBuffer.has(sign)) {
        // Get earliest data in buffer
        const earliestKey = this.apiBuffer.keys().next().value;
        const data = this.apiBuffer.get(earliestKey);
        if (data && data.pre) {
          apiStepData = data.pre;
          apiId = data.apiId;
        }
      } else {
        const data = this.apiBuffer.get(sign);
        if (data && data.post) {
          apiStepData = data.post;
          apiId = data.apiId;
        }
      }
      const onStepCallback = this.apiOnStep[apiId];
      if (onStepCallback && apiStepData) {
        onStepCallback(apiStepData);
      }

    }

    private _addApiDataToTrace(traceData: StepData[]) {
      return addApiDataToTrace({
        traceData,
        apiBuffer: this.apiBuffer,
        librariesCache: this.librariesCache,
      });
    }

  async _reRegisterApiAfterLoad() {
    this.apiReRegistrationPromise = getAwaitablePromise();

    await Promise.all(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      [...this.librariesCache.entries()].map(([apiId, apiData]) =>
        this.registerApi(apiData, true)
      )
    );


    this.apiReRegistrationPromise.resolve();
  }

  private async _sendControlEvents(messageId: string, withDelete: boolean = true) {
    // Send control events map directly via service worker channel
    const controlEvents = Object.fromEntries(this.controlEventsView);
    const interrupt = this.stepDataOnInterruptPromise !== null;
    if (withDelete) {
      this.controlEventsView.clear();
    }
    await writeMessage(this.swChannel, { controlEvents, interrupt }, messageId);
  }

  /**
   * Snapshot the graphics worker's shape map. Resolves to `{}` on no
   * worker, miss, or 1s timeout. Returned map is a deep copy.
   */
  async getCanvasState(): Promise<Record<ShapeId, GraphicsShape>> {
    if (!this.graphicsWorker) {
      return {};
    }
    return new Promise((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        if (event.data.type === CanvasAndWorkerEvent.PROVIDE_CANVAS_STATE) {
          channel.port1.close();
          resolve(event.data.shapes ?? {});
        }
      };
      setTimeout(() => {
        channel.port1.close();
        resolve({});
      }, 1000);
      this.graphicsWorker.postMessage(
        {
          type: CanvasAndWorkerEvent.REQUEST_CANVAS_STATE,
          responsePort: channel.port2,
        },
        [channel.port2],
      );
    });
  }

  // Request canvas history from graphics worker
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  private async _getCanvasHistory(): Promise<Map<any, any>> {
    if (!this.graphicsWorker) {
      return new Map();
    }

    return new Promise((resolve) => {
      const channel = new MessageChannel();

      // Set up listener for response
      channel.port1.onmessage = (event) => {
        if (event.data.type === CanvasAndWorkerEvent.PROVIDE_STEP_MAP) {
          const canvasHistory = new Map(event.data.canvasHistory || []);
          channel.port1.close();
          resolve(canvasHistory);
        }
      };

      // Timeout after 1 second
      setTimeout(() => {
        channel.port1.close();
        resolve(new Map());
      }, 1000);

      // Send request via the port
      this.graphicsWorker.postMessage({
        type: CanvasAndWorkerEvent.REQUEST_STEP_MAP,
        responsePort: channel.port2
      }, [channel.port2]);
    });
  }

  // Handle interrupt step data from worker. The worker has already
  // normalised stdout into the same `string[]` format the FINISHED path
  // produces (via `normalizeOutputHistory`), so the only thing left to do
  // here is fetch canvas history from the graphics worker and merge it
  // into the trace. Keeping a single `string[]` wire format for both the
  // FINISHED and SEND_INTERRUPT_STEP_DATA paths is what prevents the
  // format-drift bug that produced #2711.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  private async _handleInterruptStepData(data: any) {
    const fromTests = false;
    // FINISHED can clear the field during the canvas-history await below,
    // so resolve the promise interruptCode is awaiting, not the field.
    const pending = this.stepDataOnInterruptPromise;
    if (!pending) {
      console.warn("No interrupt promise to resolve");
      return;
    }

    try {
      const { traceList, outputList, errorList, fromTests } = data;

      // Request canvas history from graphics worker
      const canvasHistory = await this._getCanvasHistory();

      const tracesWithCanvas = mergeCanvasIntoTrace({
        traceList: traceList || [],
        canvasHistory,
      });

      pending.resolve({
        traceList: tracesWithCanvas,
        outputList: outputList || [],
        errorList: errorList || [],
        fromTests
      });
    } catch (error) {
      console.error("Error handling interrupt step data:", error);
      pending.resolve({
        traceList: [],
        outputList: [],
        errorList: [],
        fromTests
      });
    } finally {
      if (this.stepDataOnInterruptPromise === pending) {
        this.stepDataOnInterruptPromise = null;
      }
    }
  }


      terminate() {
        this.loadWatch?.settle();
        this.worker?.removeEventListener("message", this.handleMessageFromWorker);
        this.worker?.terminate();
        // remove all listeners
        this.canvas = null;
        this.worker = null;
        this.graphicsWorker = null;
        this.apiCallbacks = {};
        this.messageBacklog = [];
        this.librariesCache.clear();
    }



}
